import { QueryErrorState } from "@/api/QueryErrorState";
import { useAssetVersionQuery } from "@/api/library";
import {
  CHANNEL_LABELS,
  SEVERITY_LABELS,
  isAlertMatrixContent,
} from "@/lib/alertMatrix";
import { Badge } from "@/ui/Badge";
import { LoadingState } from "@/ui/states";

import type { PinConsequenceProps } from "./consequences";

/**
 * What pinning an alert matrix does to a project (#373).
 *
 * The one real implementation of the consequence seam, and the type chosen
 * because it is the only pin with an existing consequence surface today
 * (`ui/library/MatrixPinning.tsx` pins one; nothing until now said what the pin
 * MEANS from the project's side).
 *
 * WHERE THE ANSWER COMES FROM. The pin says `assetId` and `version`; what that
 * version DOES is in its stored content, which the contract serves only at an
 * explicit version — there is deliberately no "content at latest" read
 * anywhere in it. So this reads exactly the version the project pinned, and
 * every sentence below is a count or a label off that document. Nothing is
 * derived, nothing is inferred from a value, and no rule is re-evaluated here:
 * the engine decides what fires, this says what the engine was given.
 *
 * WHAT IT DOES NOT DO. It holds no adoption state — the pin arrives as a prop
 * from the Summary's single `useProjectAdoptionsQuery` — and it renders no
 * control. Pinning, moving and unpinning are #372's surface and the library's.
 */
export function AlertMatrixConsequence({ pin, asset }: PinConsequenceProps) {
  const version = useAssetVersionQuery(pin.assetId, pin.version);

  if (version.isPending) {
    return (
      <LoadingState
        label={`Loading what ${asset.name} ${pin.version} does`}
        rows={2}
      />
    );
  }

  if (version.isError) {
    return (
      <QueryErrorState
        error={version.error}
        subject="Alert matrix consequences"
        title="What this pin does could not be loaded"
        // Stated rather than glossed: the pin itself is known and rendered
        // above this block, so the honest failure is "the pinned version's
        // content did not load", not "this project has no alert matrix".
        description={`This project pins ${asset.name} at ${pin.version}. The content of that version could not be read, so what it raises and where it routes is not shown — it is not absent.`}
        onRetry={() => void version.refetch()}
      />
    );
  }

  const { content } = version.data;
  if (!isAlertMatrixContent(content)) {
    // The catalog said `alert_matrix` and the version detail disagreed. Two
    // server-owned facts that cannot both be right, so neither is presented as
    // if it were: nothing is rendered from the content, and the disagreement
    // is what the clinician is told.
    return (
      <p role="alert" className="text-sm text-destructive">
        {asset.name} {pin.version} is catalogued as an alert matrix but its
        stored content is not one, so nothing here describes what it raises.
      </p>
    );
  }

  const combos = content.combos ?? [];

  return (
    <div className="space-y-3 text-sm">
      <p className="text-foreground">
        Alerts in this project are raised and routed by{" "}
        <strong>{asset.name}</strong> at {pin.version}. That version is
        immutable, so publishing a newer one does not move this pin.
      </p>

      <ul className="grid gap-2 sm:grid-cols-3">
        <li className="rounded-md border border-border px-3 py-2">
          <span className="block text-lg font-semibold text-foreground">
            {content.domains.length}
          </span>
          <span className="text-muted-foreground">
            {content.domains.length === 1
              ? "clinical domain watched"
              : "clinical domains watched"}
          </span>
        </li>
        <li className="rounded-md border border-border px-3 py-2">
          <span className="block text-lg font-semibold text-foreground">
            {combos.length}
          </span>
          <span className="text-muted-foreground">
            {combos.length === 1
              ? "cross-domain escalation"
              : "cross-domain escalations"}
          </span>
        </li>
        <li className="rounded-md border border-border px-3 py-2">
          <span className="block text-lg font-semibold text-foreground">
            {content.destinationRefs.length}
          </span>
          <span className="text-muted-foreground">
            {content.destinationRefs.length === 1
              ? "alert destination"
              : "alert destinations"}
          </span>
        </li>
      </ul>

      <div className="space-y-1.5">
        <p className="font-medium text-foreground">Where each severity goes</p>
        <ul className="divide-y divide-border rounded-md border border-border">
          {content.routing.map((entry) => (
            <li
              key={entry.severity}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2"
            >
              <span className="min-w-0 flex-1 text-foreground">
                {SEVERITY_LABELS[entry.severity] ?? entry.severity}
              </span>
              {entry.channels.map((channel) => (
                <Badge key={channel} tone="outline">
                  {CHANNEL_LABELS[channel] ?? channel}
                </Badge>
              ))}
            </li>
          ))}
        </ul>
        {/*
          Destination REFERENCES are named, never their values. The contract
          keeps `destinationRefs` slug-shaped precisely so an address, a number
          or a webhook URL cannot be stored in one, and #25 owns resolving a
          slug to the thing it points at. Counting them above and not listing
          them here is that boundary held: this surface can say how many places
          an alert reaches without claiming to know where any of them is.
        */}
        <p className="text-xs text-muted-foreground">
          A severity with no row here raises no alert. Destinations are
          references resolved outside this editor, so they are counted rather
          than named.
        </p>
      </div>

      <div className="space-y-1.5">
        <p className="font-medium text-foreground">What each domain watches</p>
        <ul className="divide-y divide-border rounded-md border border-border">
          {content.domains.map((domain) => (
            <li key={domain.key} className="px-3 py-2">
              <span className="font-medium text-foreground">
                {domain.displayName}
              </span>
              <ul className="mt-1 space-y-0.5 text-muted-foreground">
                {domain.rules.map((rule) => (
                  <li key={`${domain.key}-${rule.flag}`}>
                    {rule.label ??
                      `${rule.minCount} or more ${rule.flag} flags → ${
                        SEVERITY_LABELS[rule.severity] ?? rule.severity
                      }`}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </div>

      {combos.length > 0 ? (
        <div className="space-y-1.5">
          <p className="font-medium text-foreground">
            Escalations across domains
          </p>
          <ul className="space-y-0.5 text-muted-foreground">
            {combos.map((combo, index) => (
              <li key={`${combo.flag}-${combo.minDomains}-${index}`}>
                {combo.label ??
                  `${combo.flag} flags in at least ${combo.minDomains} domains → ${
                    SEVERITY_LABELS[combo.severity] ?? combo.severity
                  }`}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
